import { create } from "zustand";
import { FrameworkWizardStore, StepOneData, StepTwoData, StepThreeData, StepFourData } from "../types";
import { addNotification } from "@/shared/states/notification";

const initialStepOneData: StepOneData = {};
const initialStepTwoData: StepTwoData = {};
const initialStepThreeData: StepThreeData = {};
const initialStepFourData: StepFourData = {};

export const useFrameworkWizardStore = create<FrameworkWizardStore>((set, get) => ({
  // Step Management
  currentStep: 1,
  completedSteps: [],
  totalSteps: 4,
  
  // Form Data
  frameworkName: "",
  stepOneData: initialStepOneData,
  stepTwoData: initialStepTwoData,
  stepThreeData: initialStepThreeData,
  stepFourData: initialStepFourData,
  
  // UI States
  isLoading: false,
  isSaving: false,
  isPublishing: false,
  isWizardOpen: false,
  validationErrors: {},
  
  // Step Management Actions
  setCurrentStep: (step: number) => set({ currentStep: step }),
  setFirstStepValidationError: (firstStepValidationError: boolean) => set({firstStepValidationError: firstStepValidationError}),
  markStepCompleted: (step: number) => set((state) => {
    const completedSteps = [...state.completedSteps];
    if (!completedSteps.includes(step)) {
      completedSteps.push(step);
    }
    return { completedSteps };
  }),
  
  // Form Data Actions
  setFrameworkName: (name: string) => set((state) => {
    const newValidationErrors = { ...state.validationErrors };
    delete newValidationErrors.frameworkName;
    
    return {
      frameworkName: name,
      validationErrors: newValidationErrors
    };
  }),
  
  updateStepOneData: (data: Partial<StepOneData>) => 
    set((state) => ({ 
      stepOneData: { ...state.stepOneData, ...data } 
    })),
    
  updateStepTwoData: (data: Partial<StepTwoData>) => 
    set((state) => ({ 
      stepTwoData: { ...state.stepTwoData, ...data } 
    })),
    
  updateStepThreeData: (data: Partial<StepThreeData>) => 
    set((state) => ({ 
      stepThreeData: { ...state.stepThreeData, ...data } 
    })),
    
  updateStepFourData: (data: Partial<StepFourData>) => 
    set((state) => ({ 
      stepFourData: { ...state.stepFourData, ...data } 
    })),
  
  // UI State Actions
  setIsLoading: (loading: boolean) => set({ isLoading: loading }),
  setIsSaving: (saving: boolean) => set({ isSaving: saving }),
  setIsPublishing: (publishing: boolean) => set({ isPublishing: publishing }),
  setValidationErrors: (errors: Record<string, string>) => set({ validationErrors: errors }),
  
  // Wizard visibility actions
  openWizard: () => set({ isWizardOpen: true }),
  closeWizard: () => set({ isWizardOpen: false }),
  
  // Complex Operations
  validateStep: (step: number): boolean => {
    const state = get();
    const errors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        if (!state.frameworkName.trim()) {
          errors.frameworkName = "Framework name is required";
          addNotification({
            type: "error",
            message: "Framework name is required",
          });
        }
        if (state.frameworkName.trim() && state.frameworkName.trim().length < 4) {
          errors.frameworkName = "Framework name must be at least 4 characters long";
          addNotification({
            type: "error",
            message: "Framework name must be at least 4 characters long",
          });
        }
        
        // Add other step 1 validations
        break;
      case 2:
        // Add step 2 validations
        if (!state.stepTwoData.frameworkId) {
          errors.framework = "Framework must be created first";
          addNotification({
            type: "error",
            message: "Please complete Step 1 first",
          });
        }
        break;
      case 3:
        // Add step 3 validations
        break;
      case 4:
        // Step 4 validations
        if (!state.frameworkName.trim()) {
          errors.frameworkName = "Framework name is required";
          addNotification({
            type: "error",
            message: "Framework name is required for publishing",
          });
        }
        if (!state.stepTwoData.frameworkId) {
          errors.framework = "Framework ID is required";
          addNotification({
            type: "error",
            message: "Framework ID not found.",
          });
        }
        break;
    }
    
    set({ validationErrors: errors });
    return Object.keys(errors).length === 0;
  },
  
  resetWizard: () => set({
    currentStep: 1,
    completedSteps: [],
    frameworkName: "",
    stepOneData: initialStepOneData,
    stepTwoData: initialStepTwoData,
    stepThreeData: initialStepThreeData,
    stepFourData: initialStepFourData,
    isLoading: false,
    isSaving: false,
    isPublishing: false,
    isWizardOpen: false,
    validationErrors: {},
  }),
}));
